import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import {
  getErrorsLoadingIndividualsByUserIds,
  getIndividualsByUserIds,
  getLoadingIndividualUserIds,
  populateIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/_shared/masked-fields";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const ProposalContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
  display: "grid",
  gap: theme.spacing(6),
  gridTemplateColumns: "auto auto",
  height: "100%",
  width: "100%",
}));
const GeneralSection = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& .MuiFormControl-root": {
    marginTop: theme.spacing(1),
  },
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(50),
}));
const NameTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(50),
}));
const DescriptionTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(50),
}));
const DateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  margin: theme.spacing(1, 0),
  width: "100%",
}));

type DetailsTabProps = {
  proposal: Proposal;
  commentThreads: CommentThread[];
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const { proposal, commentThreads, onCommentsClicked } = props;

  const dispatch = useAttorneyHubDispatch();
  const session = useSession();

  const individualUserIds = useMemo(() => {
    const ids: Guid[] = [];
    if (Masked.isPresentNotMasked(proposal.clientLeader)) {
      ids.push(proposal.clientLeader.userId);
    }
    return ids;
  }, [proposal.clientLeader]);

  const individualsByUserIds = useSelector(getIndividualsByUserIds(individualUserIds));
  const loadingIndividuals = useSelector(getLoadingIndividualUserIds());
  const errorsLoadingIndividuals = useSelector(getErrorsLoadingIndividualsByUserIds(individualUserIds));

  const missingIndividuals = useMemo(
    () =>
      individualUserIds.filter(
        (userId) =>
          !loadingIndividuals.includes(userId) &&
          !errorsLoadingIndividuals?.[userId.value] &&
          !individualsByUserIds?.some((individual) => individual.userId.isEqualTo(userId)),
      ),
    [individualUserIds, loadingIndividuals, errorsLoadingIndividuals, individualsByUserIds],
  );

  useEffect(() => {
    if (missingIndividuals.length === 0) return;
    dispatch(populateIndividualsByUserIds({ session, userIds: missingIndividuals }));
  }, [dispatch, missingIndividuals, session]);

  const clientName = Masked.isMasked(proposal.clientLeader)
    ? "Insufficient Permissions to View Client"
    : (individualsByUserIds?.find((individual) =>
        individual.userId.isEqualTo((proposal.clientLeader as EntityMember)?.userId),
      )?.fullName ?? "");

  const getInputAdornment = (field: ProposalField) => (
    <CommentsButton
      commentThreads={commentThreads}
      hideUnreadCount={true}
      field={field}
      toolTip={commentThreads?.some((thread) => thread.field.isEqualTo(field)) ? "Comments" : "No Comments"}
      onCommentsClicked={
        onCommentsClicked
          ? () => {
              onCommentsClicked(field);
            }
          : undefined
      }
    />
  );

  return (
    <ProposalContainer>
      <GeneralSection>
        <ClientTextField
          disabled={true}
          label={proposal.clientLeader ? "Client Representative" : "Select Client Representative"}
          error={false}
          required
          value={clientName}
          slotProps={{
            input: {
              endAdornment: (
                <CommentsButton
                  commentThreads={commentThreads}
                  hideUnreadCount={true}
                  field={ProposalField.Client}
                  toolTip={
                    commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Client))
                      ? "Comments"
                      : "No Comments"
                  }
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Client);
                        }
                      : undefined
                  }
                />
              ),
            },
          }}
        />
        <NameTextField
          label="Proposal Name"
          required={true}
          disabled={true}
          value={proposal.name?.value ?? ""}
          error={false}
          slotProps={{
            input: {
              endAdornment: (
                <CommentsButton
                  field={ProposalField.Name}
                  hideUnreadCount={true}
                  commentThreads={commentThreads}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Name);
                        }
                      : undefined
                  }
                  toolTip={
                    commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Name))
                      ? "Comments"
                      : "No Comments."
                  }
                />
              ),
            },
          }}
        />
        <DescriptionTextField
          label="Description"
          required={true}
          multiline={true}
          rows={3}
          disabled={true}
          value={proposal.description?.value ?? ""}
          error={false}
          slotProps={{
            input: {
              endAdornment: (
                <CommentsButton
                  commentThreads={commentThreads}
                  hideUnreadCount={true}
                  field={ProposalField.Description}
                  toolTip={
                    commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Description))
                      ? "Comments"
                      : "No Comments"
                  }
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Description);
                        }
                      : undefined
                  }
                />
              ),
            },
          }}
        />
      </GeneralSection>
      <DateSection>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.responseDueBy?.asMoment ?? null}
            label="Response Due Date"
            slots={{
              inputAdornment: () => getInputAdornment(ProposalField.ResponseDueBy),
            }}
          />
        </DateContainer>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.startDate?.asMoment}
            label={"Start Date"}
            slots={{
              inputAdornment: () => getInputAdornment(ProposalField.StartDate),
            }}
          />
        </DateContainer>
        <DateContainer>
          <DatePicker
            disabled={true}
            value={proposal.endDate?.asMoment}
            label="End Date"
            slots={{
              inputAdornment: () => getInputAdornment(ProposalField.EndDate),
            }}
          />
        </DateContainer>
      </DateSection>
    </ProposalContainer>
  );
}
